/* src/utils/embedTheme.js
 * Frontend color utilities for Phase B host-context adaptive theming.
 *
 * Color thresholds ported from functions-forms/src/utils/websiteScraper.ts
 * (isUsableBrandColor, relativeLuminance, rgbToHsl) so the same guard logic
 * runs on both sides of the iframe boundary.
 */

/**
 * Parse a CSS color string into [r, g, b] (0-255 each).
 * Handles:
 *   rgb(r, g, b)
 *   rgba(r, g, b, a)   — returns null when fully transparent (a === 0)
 *   #rgb  / #rrggbb
 * Returns null for anything unparseable.
 */
export function parseCssColor(str) {
  if (!str || typeof str !== 'string') return null;
  const s = str.trim();

  // rgb(...) / rgba(...)
  const rgbMatch = s.match(
    /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/
  );
  if (rgbMatch) {
    const alpha = rgbMatch[4] !== undefined ? parseFloat(rgbMatch[4]) : 1;
    if (alpha === 0) return null; // fully transparent
    return [
      parseInt(rgbMatch[1], 10),
      parseInt(rgbMatch[2], 10),
      parseInt(rgbMatch[3], 10),
    ];
  }

  // #rgb or #rrggbb
  const hexMatch = s.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (hexMatch) {
    let h = hexMatch[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const n = parseInt(h, 16);
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
  }

  return null;
}

/**
 * WCAG 2.1 relative luminance of an [r, g, b] triple.
 * Matches the formula in websiteScraper.ts.
 */
export function relativeLuminance([r, g, b]) {
  const toLinear = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * HSL saturation component from [r, g, b].
 */
function saturation([r, g, b]) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return 0;
  const d = max - min;
  return l > 0.5 ? d / (2 - max - min) : d / (max + min);
}

/**
 * Returns true when the color is a safe card-surface candidate.
 * Rejects:
 *   lum > 0.92  — near-white (indistinguishable from default white card)
 *   sat < 0.15  — unsaturated: black, gray, near-black with no hue
 * Saturated darks (navy, dark purple, deep teal) pass.
 *
 * Thresholds match isUsableBrandColor in websiteScraper.ts lines 50-51.
 * Hard rule: only colors that pass this guard may be adopted as the card
 * surface — so Phase B can never make the form less readable than Phase A.
 */
export function isUsableSurface(rgb) {
  const lum = relativeLuminance(rgb);
  const sat = saturation(rgb);
  if (lum > 0.92) return false; // near-white
  if (sat < 0.15) return false; // unsaturated (gray, black, near-white)
  return true;
}

/**
 * Given a background [r, g, b], return a contrasting foreground text color.
 * Luminance threshold 0.179 ≈ midpoint on the perceptual lightness scale.
 */
export function pickReadableText(rgb) {
  return relativeLuminance(rgb) > 0.179 ? '#1a1a1a' : '#ffffff';
}
